/**
 * @fileoverview Fetch the theme a page points at, if there is one and if it is
 * reachable.
 *
 * SEPARATE FROM `loadPage` ON PURPOSE, and the reason is what a reader sees
 * when a relay is slow.
 *
 * A theme is decoration. If it were fetched inside `loadPage`, a page whose
 * theme relay is unreachable would sit on a loading indicator for the full
 * relay timeout before showing a single word of text -- the body was in hand
 * the whole time. So the page renders first, in default tokens, and the theme
 * is applied when it arrives. An author sees a brief moment of unstyled prose;
 * a reader on a bad connection sees the article instead of a spinner. That
 * trade is the right way round for something whose entire job is to be read.
 *
 * IT DOES NOT REOPEN THE TIMING CHANNEL that `loadPage` closes. Every failure
 * there is held to a floor so a stranger cannot tell a private page from a
 * missing one by how fast the error came back. Nothing here runs until that
 * decision is already made and the page is known to be renderable, so the time
 * this takes says nothing about whether a page exists. A theme that is missing,
 * malformed, or on a dead relay is simply no theme.
 *
 * ONLY OUR THEME KIND IS FETCHED. A `theme` tag naming some other addressable
 * kind is a real reference to something real that is not a theme, and following
 * it would mean handing an arbitrary event to a parser looking for style
 * values. Refused before the query rather than after it, so a page cannot use
 * its theme tag to make a reader's browser fetch an arbitrary event.
 */

import { PAGE_THEME_KIND } from './kinds';
import {
  parseThemeAddress,
  parseThemeEvent,
  type PageTheme,
  type RawThemeEvent,
} from './theme';

import { fetchNewest } from '@/services/nostr/relays';

/**
 * The theme for a page, or null.
 *
 * Null covers every way this can come to nothing: no tag, a malformed
 * coordinate, a coordinate naming the wrong kind, no relay holding the event,
 * an event that is not a theme. None of those is distinguished, because none of
 * them changes what the reader gets, which is the page in its default tokens.
 */
export async function loadTheme(
  coordinate: string | null,
  relays: string[]
): Promise<PageTheme | null> {
  const found = await loadThemeEvent(coordinate, relays);
  return found && parseThemeEvent(found, PAGE_THEME_KIND);
}

/**
 * The theme's EVENT, checked against the address it was asked for, or null.
 *
 * Exported for the theme editor, which needs the tags themselves and not only
 * what this app's parser understood of them: an editor that rebuilt a theme
 * from the parsed form alone would drop any tag it has no control for, which is
 * the data loss the page editor was shaped to avoid.
 *
 * The address check lives here rather than in either caller, so an editor and a
 * reader cannot come to disagree about whether a relay's answer is the thing
 * that was requested.
 */
export async function loadThemeEvent(
  coordinate: string | null,
  relays: string[]
): Promise<RawThemeEvent | null> {
  if (!coordinate) return null;

  const address = parseThemeAddress(coordinate);
  if (!address) return null;
  if (address.kind !== PAGE_THEME_KIND) return null;

  const event = await fetchNewest(relays, {
    kinds: [address.kind],
    authors: [address.pubkey],
    '#d': [address.identifier],
  });
  if (!event) return null;

  const theme = parseThemeEvent(event, PAGE_THEME_KIND);
  if (!theme) return null;

  // THE ADDRESS IS AUTHORITATIVE, NOT THE RELAY'S ANSWER. A relay is asked for
  // one author's theme and hands back whatever it likes; the signature it
  // carries proves only that SOMEBODY signed it, not that it is the one the
  // page asked for. Without this, a relay could style every page it serves with
  // a theme of its own choosing and the page's own `theme` tag would be
  // decoration. The same reasoning as verifying a blob against its hash: the
  // thing we asked for is named in the request, so check the answer against it.
  if (theme.address.pubkey !== address.pubkey) return null;
  if (theme.address.identifier !== address.identifier) return null;

  return event;
}
